import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./Login.css";

function LogoMark({ size = 32 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="login-brand__mark"
    >
      <path
        d="M4 20V8.5C4 7.67 4.41 6.9 5.1 6.43L11.1 2.34C11.65 1.97 12.35 1.97 12.9 2.34L18.9 6.43C19.59 6.9 20 7.67 20 8.5V20"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M9 20V13.5C9 12.67 9.67 12 10.5 12H13.5C14.33 12 15 12.67 15 13.5V20"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M7.5 10.5H16.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function InputIconUser() {
  return (
    <svg
      className="login-field__icon"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      {/* Head */}
      <circle
        cx="12"
        cy="8"
        r="4"
        stroke="currentColor"
        strokeWidth="1.6"
      />

      {/* Closed body shape */}
      <path
        d="M6 20v-1c0-3 3-5 6-5s6 2 6 5v1H6Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function InputIconMail() {
  return (
    <svg
      className="login-field__icon"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path d="M4 6h16v12H4V6Z" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="m4 7 8 6 8-6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function InputIconLock() {
  return (
    <svg
      className="login-field__icon"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M7 11V8a5 5 0 0 1 10 0v3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M6 11h12v9H6v-9Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function Signup() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const res = await fetch("/api/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username, email, password }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || "Signup failed");
      }

      if (data.verification_required) {
        if (data.email_sent === false && data.email_error) {
          setError(
            `Account created, but we could not send the verification email (${data.email_error}). ` +
              'Open Sign in and use "Resend verification email", or contact support.',
          );
          return;
        }
        const q = new URLSearchParams({
          verify: "sent",
          email: email || data.email || "",
        });
        navigate(`/login?${q.toString()}`, { replace: true });
        return;
      }

      await refreshUser();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.message || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-shell">
      <div className="login-shell__bg" aria-hidden="true">
        <div className="login-shell__blob login-shell__blob--tr" />
        <div className="login-shell__blob login-shell__blob--bl" />
      </div>

      <div className="login-shell__grid">
        <aside className="login-brand">
          <div className="login-brand__logo">
            <LogoMark />
            <span className="login-brand__name">
              Zyft<span className="login-brand__dot">.AI</span>
            </span>
          </div>

          <h1 className="login-brand__title">
            Create your
            <br />
            <span className="login-brand__accent font-serif">
              engagement workspace access
            </span>
          </h1>

          <p className="login-brand__subtitle">
            Sign up for an account to start sending AI-powered greetings and
            managing your team&apos;s celebrations.
          </p>
        </aside>

        <section className="login-card">
          <div className="login-card__mobileBrand">
            <LogoMark size={26} />
            <span className="login-brand__name login-brand__name--sm">
              Zyft<span className="login-brand__dot">.AI</span>
            </span>
          </div>

          <div className="login-card__header">
            <h2 className="login-card__title">Sign Up</h2>
            <p className="login-card__hint">
              Create an account to access your organization&apos;s workspace
            </p>
          </div>

          {error && (
            <div className="login-error" role="alert">
              <div className="login-error__icon" aria-hidden="true">
                !
              </div>
              <p className="login-error__text">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            
            <div className="login-field">
              <label className="login-field__label" htmlFor="username">
                Full name
              </label>
              <div className="login-field__control">
                <InputIconUser/>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Alex Johnson"
                  required
                />
              </div>
            </div>

            <div className="login-field">
              <label className="login-field__label" htmlFor="email">
                Work email
              </label>
              <div className="login-field__control">
                <InputIconMail />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                />
              </div>
            </div>

            <div className="login-field">
              <label className="login-field__label" htmlFor="password">
                Password
              </label>
              <div className="login-field__control">
                <InputIconLock />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a strong password"
                  required
                />
              </div>
            </div>

            <div className="login-field">
              <label className="login-field__label" htmlFor="confirmPassword">
                Confirm password
              </label>
              <div className="login-field__control">
                <InputIconLock />
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setError(null);
                  }}
                  placeholder="Re-enter your password"
                  required
                />
              </div>
            </div>
            <button className="login-submit" type="submit" disabled={loading}>
              {loading ? "Creating account..." : "Create account"}
            </button>
          </form>

          <div className="login-card__footer">
            <p className="login-card__signup">
              Already have an account? <Link to="/login">Sign in</Link>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

